/**
 * Shared focus-ring styles — the single source of truth for the design
 * system's focus treatment (DS-584).
 *
 * One treatment everywhere: outset 2px ring + border-colour swap, keyed to
 * `focus-visible` (never bare `focus:`), native outline removed. Two built-in
 * clipping defences:
 * - `focus-visible:z-10` lifts the element above later-painting siblings that
 *   would cover the ring (needs a flex/grid item or positioned element; not
 *   `focus-visible:relative` here — it would override `absolute` on focus).
 * - The border swap stays visible when an `overflow` ancestor crops the halo
 *   (borders can't be clipped). Give scroll containers breathing room
 *   (e.g. `py-2 -my-2`) so the halo isn't cropped.
 *
 * @see ../../../../.ai-context/design-system/design-tokens.md — "Focus rings"
 */

/**
 * Element-level focus ring for components that are themselves focusable
 * (buttons, inputs, toggles, custom clickable surfaces). Ring and border swap
 * share `border-focus/50` so they read as one soft outline.
 *
 * Components that clip their fill (`bg-clip-padding`) must pair this with
 * `focus-visible:bg-clip-border`, or the `/50` swap composites against the
 * ring's shadow-knockout edge into a dark seam — or use {@link focusRingSolid}.
 * Button's gutter no longer clips, so this only applies to a component that
 * reintroduces the clip for its own reasons.
 */
export const focusRing =
  'outline-none focus-visible:outline-none focus-visible:z-10 focus-visible:border-border-focus/50 focus-visible:ring-2 focus-visible:ring-border-focus/50';

/**
 * Solid focus treatment for border-gutter components (DS-787): opaque 1px
 * `border-focus` border + 1.5px `border-focus/60` halo — a 2.5px indicator,
 * tight to the element's edge.
 *
 * The line is `border-focus` at full strength, the same token as the form
 * controls' `control-border-focus`, so a focused button and a focused field
 * draw the same line. **The border must stay opaque**: a semi-transparent swap
 * composites against the fill (navy tint on dark fills) or the ring's
 * shadow-knockout edge (dark seam), which is why no `bg-clip` juggling is
 * needed on focus.
 *
 * The halo is the *same* token at `/60`, so it fades from the line into
 * whatever sits behind the button rather than stepping onto a second tone.
 * One token, one hue, a gradient edge in every theme. The alpha is safe here
 * where it is not on the border: an outer ring is a box-shadow, knocked out
 * under the border box, so it composites against the page, never against the
 * fill.
 *
 * A notch heavier than {@link focusRing}'s `/50` on purpose — that halo is 2px
 * and hangs off an equally-faint `/50` border, where this one is 1.5px against
 * an opaque line. Matching `/50` here left the thinner ring reading as a gap
 * between the line and the page rather than a continuation of it.
 *
 * Contrast still rides on the opaque 1px line (WCAG 2.4.11), so softening the
 * halo costs the indicator nothing.
 *
 * (DS-787 originally cut two extra tokens for the halo — `border-focus-inner`,
 * then `border-focus-halo` one skies step below the line. Both were deleted
 * once the line's own alpha did the job; `border-focus` is now the only focus
 * colour token in the system, in every theme.)
 *
 * The gutter is what makes the border half of this render at all:
 * `focus-visible:border-*` is a colour-only utility, so over a 0px border it is
 * a silent no-op (measured on the stable Button — `:focus-visible` matches, the
 * computed border stays `0px`).
 */
export const focusRingSolid =
  'outline-none focus-visible:outline-none focus-visible:z-10 focus-visible:border-border-focus focus-visible:ring-[1.5px] focus-visible:ring-border-focus/60';

/**
 * Focus ring for a wrapper whose focusable element is a descendant (e.g. the
 * wrapper around a borderless `<input>`). Prefer {@link focusRing} whenever
 * the styled element is itself focusable.
 */
export const focusRingWithin =
  'outline-none has-[:focus-visible]:z-10 has-[:focus-visible]:border-border-focus/50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-border-focus/50';

/**
 * Invalid-state ring for form fields — {@link focusRing}'s shape in the
 * destructive colour, keyed to `aria-invalid="true"`. The arbitrary
 * `aria-[invalid='true']:` form (not `aria-invalid:`) keeps the eslint
 * Tailwind plugin happy with our semantic tokens.
 */
export const focusRingInvalid =
  "aria-[invalid='true']:border-validation-destructive aria-[invalid='true']:ring-2 aria-[invalid='true']:ring-validation-destructive/20";

/**
 * DS-1527 — {@link focusRingInvalid} on the reworked tier, for
 * `*.experimental.styles.ts` recipes behind
 * `release-experimental-design-system-forms`. Same shape, same `/20` halo.
 *
 * A sibling rather than a repoint of {@link focusRingInvalid}: that constant is
 * also imported by `checkbox.styles.ts`, a **stable** recipe, and the semantic
 * tier is confined to experimental recipes until the cleanup codemod. The two
 * collapse back into one at flag cleanup.
 *
 * The token is `semantic-border-destructive-strong`, the tone equivalent of the
 * neutral ramp's `border` → `border-strong` pair. An invalid border is a
 * non-text graphical indicator and owes 3:1 to the field fill (WCAG 1.4.11);
 * against `bg-surface` the strong role is 3.91:1 newBrand / 4.58:1 newBrandDark.
 *
 * Not plain `semantic-border-destructive` (red/4, 1.28:1 / 1.41:1) — that is a
 * feather border for banners and outline toasts, where adjacent text carries
 * the meaning, and cannot carry an indicator on its own.
 *
 * Not `semantic-fill-destructive` either, even though it holds the same value:
 * that is the role for an object's painted interior (a progress bar, a status
 * dot). Borrowing it to paint a border would violate the one-property-per-token
 * rule in token-taxonomy.md — the same ambiguity that made `validation-*`
 * unmaintainable.
 */
export const focusRingInvalidExperimental =
  "aria-[invalid='true']:border-semantic-border-destructive-strong aria-[invalid='true']:ring-2 aria-[invalid='true']:ring-semantic-border-destructive-strong/20";
